
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import { Svg, Path } from 'react-native-svg';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import CircularArrowButton from '@/components/CircularArrowButton';
import { TempOnboardingService } from '@/services/tempOnboardingService';

type GenderOption = 'feminino' | 'masculino' | 'nao_binario' | 'prefiro_nao_dizer';

export default function OnboardingGenderScreen() {
  const { logCompleted } = useOnboardingStepTracking('gender');
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [selectedGender, setSelectedGender] = useState<GenderOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load previously saved gender preference on mount and dismiss keyboard
  useEffect(() => {
    Keyboard.dismiss();
    const loadSavedGender = async () => {
      const data = await TempOnboardingService.getData();
      if (data.gender) {
        setSelectedGender(data.gender);
        console.log('📥 Loaded previously saved gender:', data.gender);
      }
    };
    loadSavedGender();
  }, []);

  const genderOptions = [
    { value: 'feminino' as GenderOption, label: t('onboarding.gender.feminine') },
    { value: 'masculino' as GenderOption, label: t('onboarding.gender.masculine') },
    { value: 'nao_binario' as GenderOption, label: t('onboarding.gender.nonBinary') },
    { value: 'prefiro_nao_dizer' as GenderOption, label: t('onboarding.gender.preferNotToSay') },
  ];

  const handleOptionSelect = (option: GenderOption) => {
    setSelectedGender(option);
  };

  const handleContinue = async () => {
    if (!selectedGender) {
      Alert.alert(t('onboarding.gender.required'), t('onboarding.gender.requiredMessage'));
      return;
    }

    setIsLoading(true);
    try {
      // Save gender to temporary storage (pre-auth flow)
      await TempOnboardingService.saveGender(selectedGender);
      console.log('✅ Gender saved to temporary storage (pre-auth flow):', selectedGender);

      // PRD §5.2 — `onboarding_step_completed` for the `gender` step.
      logCompleted();

      // Navigate to support-recipient step
      router.push('/onboarding-support-recipient');
    } catch (error) {
      console.error('❌ Error saving gender:', error);
      Alert.alert(
        t('onboarding.gender.error'),
        t('onboarding.gender.errorMessage')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const progressWidth = 50; // Step 5 of 10

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <View style={styles.contentContainer}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Step Section */}
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>5</Text>
            </View>

            <View style={styles.textContainer}>
              <Text style={styles.title}>{t('onboarding.gender.title')}</Text>
              <Text style={styles.subtitle}>
                {t('onboarding.gender.subtitle')}
              </Text>
            </View>
          </View>

          {/* Options Section */}
          <View style={styles.optionsSection}>
            {genderOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.card,
                  selectedGender === option.value && styles.selectedCard
                ]}
                onPress={() => handleOptionSelect(option.value)}
              >
                <Text style={styles.optionText}>
                  {option.label}
                </Text>
                <View style={[
                  styles.radioButton,
                  selectedGender === option.value && styles.selectedRadio
                ]}>
                  {selectedGender === option.value && <View style={styles.radioInner} />}
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>

        {/* Continue Button */}
        <View style={[styles.buttonContainer, { bottom: insets.bottom + 24 }]}>
          <CircularArrowButton
            onPress={handleContinue}
            disabled={!selectedGender || isLoading}
          />
        </View>
      </View>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  subtitle: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    width: 345,
  },
  optionsSection: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 12,
    flexDirection: 'column',
    width: '100%',
  },
  card: {
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    width: '100%',
    height: 64,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  selectedCard: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  optionText: {
    color: '#151515',
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6, // 140% of 14px
  },
  radioButton: {
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  selectedRadio: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#FFFFFF',
  },
  radioInner: {
    backgroundColor: '#015480',
    width: 12,
    height: 12,
    borderRadius: 6,
    flexShrink: 0,
  },
  buttonContainer: {
    position: 'absolute',
    right: 24,
  },
});
